import { Box } from '@chakra-ui/react';
import { AlertCircle } from '@kula-ai/icons-core';

export type DotStatus = 'pending' | 'active' | 'done' | 'error';

const LABEL: Record<DotStatus, string> = {
  done: 'done',
  active: 'in progress',
  pending: 'not started',
  error: 'failed',
};

interface StatusDotProps {
  /** Drives colour + aria-label. Omit and pass `color` for a plain dot. */
  status?: DotStatus;
  /** Explicit colour (token or hex) — for domain palettes like job status. */
  color?: string;
  size?: string;
}

// A filled dot for done/active, a hollow ring for pending, an alert for error.
export const StatusDot = ({ status, color, size = '9px' }: StatusDotProps) => {
  if (status === 'error') {
    return (
      <Box color="error-500" display="inline-flex" aria-label={LABEL.error}>
        <AlertCircle size={13} color="currentColor" />
      </Box>
    );
  }
  const hollow = status === 'pending';
  const bg =
    color ??
    (status === 'done'
      ? 'success-500'
      : status === 'active'
        ? 'warning-400'
        : 'transparent');
  return (
    <Box
      as="span"
      w={size}
      h={size}
      borderRadius="full"
      flexShrink={0}
      aria-label={status ? LABEL[status] : undefined}
      bg={hollow ? 'transparent' : bg}
      border={hollow ? '1.5px solid' : undefined}
      borderColor={hollow ? 'secondary-300' : undefined}
      boxShadow={
        status === 'active'
          ? '0 0 0 3px var(--chakra-colors-warning-100)'
          : undefined
      }
      className={status === 'active' ? 'animate-pulse' : undefined}
    />
  );
};
